/**
 * Deterministic pseudo-random number generator.
 *
 * Uses xoshiro128** (32-bit) seeded through splitmix32. This is a stateful,
 * *reproducible* generator: given the same seed it produces the exact same
 * stream on every platform — a hard requirement for deterministic simulation,
 * lockstep multiplayer and replays. Never use `Math.random()` in gameplay code.
 */
import type { Seed } from '../ids.js';

/** Expand a 32-bit seed into 4×32-bit state via splitmix32. */
const splitmix32 = (seed: number): [number, number, number, number] => {
  let z = seed >>> 0;
  const next = (): number => {
    z = (z + 0x9e3779b9) | 0;
    let x = z ^ (z >>> 16);
    x = Math.imul(x, 0x21f0aaad);
    x = x ^ (x >>> 15);
    x = Math.imul(x, 0x735a2d97);
    x = x ^ (x >>> 15);
    return x >>> 0;
  };
  return [next(), next(), next(), next()];
};

const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

export class Prng {
  private s0: number;
  private s1: number;
  private s2: number;
  private s3: number;

  constructor(seed: Seed) {
    const [a, b, c, d] = splitmix32(seed);
    // Guard against the all-zero state (which xoshiro cannot escape).
    this.s0 = a || 1;
    this.s1 = b;
    this.s2 = c;
    this.s3 = d;
  }

  /** Raw next 32-bit unsigned integer. */
  nextUint32(): number {
    const result = (Math.imul(rotl(Math.imul(this.s1, 5) >>> 0, 7) >>> 0, 9) >>> 0);
    const t = (this.s1 << 9) >>> 0;
    this.s2 ^= this.s0;
    this.s3 ^= this.s1;
    this.s1 ^= this.s2;
    this.s0 ^= this.s3;
    this.s2 ^= t;
    this.s3 = rotl(this.s3, 11);
    return result >>> 0;
  }

  /** Float in [0, 1). */
  nextFloat(): number {
    // 53-bit-ish precision from a single 32-bit draw is unnecessary; use 32 bits.
    return this.nextUint32() / 0x100000000;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + this.nextFloat() * (max - min);
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + Math.floor(this.nextFloat() * (max - min + 1));
  }

  /** True with the given probability p ∈ [0, 1]. */
  chance(p: number): boolean {
    return this.nextFloat() < p;
  }

  /** Uniformly pick one element; throws on empty input. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Prng.pick: empty array');
    const item = items[this.int(0, items.length - 1)];
    // Length is checked above, so index is always valid.
    return item as T;
  }

  /** Snapshot the internal state (for save games / determinism tests). */
  getState(): readonly [number, number, number, number] {
    return [this.s0, this.s1, this.s2, this.s3];
  }

  /** Restore a previously captured state. */
  setState(state: readonly [number, number, number, number]): void {
    [this.s0, this.s1, this.s2, this.s3] = state;
  }
}
